import { decrementRequestCount } from './requests'
import ReactGA from 'react-ga'
import storeService from '../api/services/storeService'
import ShippingModel from '../api/models/ShippingModel'
import { getIsUsaSelected } from '../redux/selectors/checkoutSelectors'

const setPaypalLoadedApi = loaded => ({
  type: 'SET_PAYPAL_LOADED_STATE',
  loaded
})

const setPaymentStatus = (status, orderData, GAaction, storeId, error) => {
  ReactGA.event({
    category: status === 'pending' ? 'store' : 'checkout',
    action: GAaction,
    label: storeId
  })
  return function (dispatch) {
    dispatch(decrementRequestCount())
    dispatch({
      type: 'SET_PAYMENT_STATUS',
      orderData,
      status,
      paypalError: error
    })
  }
}

const setCheckoutErrors = (status, errors) => {
  return function (dispatch) {
    dispatch(decrementRequestCount())
    dispatch({
      type: 'SET_CHECKOUT_ERRORS',
      status,
      checkoutErrors: errors
    })
  }
}

const setPaypalIsPending = status => {
  return {
    type: 'SET_PAYPAL_PENDING',
    status
  }
}

const setCancelIndicator = state => ({
  type: 'SET_CANCEL_INDICATOR',
  state
})

const setOption = (type) => {
  return dispatch => {
    return new Promise(resolve => {
      dispatch({ type })
      checkFormValidation()
      resolve()
    })
  }
}

const setPickupOption = () => setOption('SET_PICKUP_OPTION')
const setTablePickupOption = () => setOption('SET_TABLE_PICKUP_OPTION')
const setShippingOption = () => setOption('SET_SHIPPING_OPTION')
const setServeToTableOption = () => setOption('SET_SERVE_TO_TABLE_OPTION')

const setTableNumber = tableNumber => {
  return dispatch => {
    return new Promise(resolve => {
      dispatch({
        type: 'SET_TABLE_NUMBER',
        tableNumber
      })
      checkFormValidation()
      resolve()
    })
  }
}

const getShippingCountries = (storeId, products, geometry, ct) => {
  return async (dispatch) => {
    const resp = await storeService.getShippingCountries(
      storeId,
      products,
      geometry,
      ct
    )
    dispatch({
      type: 'SET_SHIPPING_COUNTRIES',
      countriesList: resp
    })
    if (resp.length > 0) {
      dispatch(setInputValue('country_code', resp[0].code_2))
      dispatch(getStatesForCountry(
        resp[0].code_2,
        resp[0].has_state_codes
      ))
    }
  }
}

const setCuratorToken = ct => ({
  type: 'SET_CURATOR_TOKEN',
  curatorToken: ct
})

const calculateCost = (storeId, products, storeGeometry, shipping = null, ct = null, table_number = '', is_table_pickup = false) => {
  products.forEach(product => product.populateCheckoutValues())
  return async dispatch => {
    try {
      const resp = await storeService.getCost(
        storeId,
        products,
        storeGeometry,
        shipping,
        ct,
        table_number,
        is_table_pickup
      )
      const timeFrom = resp.min_processing_days + resp.delivery_time_from
      const timeTo = resp.max_processing_days + resp.delivery_time_to
      dispatch(
        setCalculation(
          resp.shipping_cost,
          resp.subtotal,
          resp.tax,
          resp.total_amount,
          resp.service_fee
        )
      )
      dispatch(setShippingDeliveryTime(timeFrom, timeTo))
      dispatch(checkFormValidation())
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('error', e)
      dispatch(setApiCalculateError(e.message, 'postal_code'))
      dispatch(checkFormValidation())
    }
  }
}

const setApiCalculateError = (errorMessage, errorField) => ({
  type: 'SET_CALCULATION_API_ERROR',
  errorMessage,
  errorField
})
const setShippingDeliveryTime = (shippingMinDays, shippingMaxDays) => ({
  type: 'SET_SHIPPING_DELIVERY_TIME',
  shippingMinDays,
  shippingMaxDays
})

const setCalculation = (shippingCost, subtotal, tax, totalAmount, serviceFee) => ({
  type: 'SET_CALCULATION',
  shippingCost,
  subtotal,
  tax,
  totalAmount,
  serviceFee
})

const getStatesForCountry = (countryCode, hasStates) => {
  return async dispatch => {
    let promise
    if (hasStates) {
      promise = storeService.getCountryStates(countryCode)
        .then(resp => dispatch(setStatesForCountry(resp, true)))
    } else {
      promise = dispatch(setStatesForCountry([], false))
    }
    dispatch(setInputValue('state', ''))
    return promise
  }
}

const setStatesForCountry = (countryStates, stateRequired) => ({
  type: 'SET_STATES_FOR_COUNTRY',
  countryStates,
  stateRequired
})

const highlightInvalid = () => ({
  type: 'HIGHLIGHT_INVALID'
})

const setInputValue = (name, value) => {
  return (dispatch) => {
    dispatch(_setInputValue(name, value))
  }
}

const setPostalCodeInputValue = (
  name,
  value,
  callbackAfterCalculation = () => { }
) => {
  return (dispatch, getState) => {
    dispatch(_setInputValue(name, value))
    const state = getState()
    const isUsaCurrentState = getIsUsaSelected(state)
    if (name === 'postal_code' && isUsaCurrentState) {
      dispatch(_calculateShippingCost()).then(callbackAfterCalculation)
    }
  }
}

const setStateInputValue = (
  name,
  value,
  callbackAfterCalculation = () => { }
) => {
  return (dispatch, getState) => {
    dispatch(_setInputValue(name, value))
    const state = getState()
    const isUsaCurrentState = getIsUsaSelected(state)
    isUsaCurrentState &&
      dispatch(_calculateShippingCost()).then(callbackAfterCalculation)
  }
}

const setCountryCodeInputValue = (name, value) => {
  return dispatch => {
    dispatch(_setInputValue(name, value))
    dispatch(_setCountryStatesIfRequired()).then(item => {
      dispatch(_calculateShippingCost())
    })
  }
}

const clearCheckout = () => ({
  type: 'CLEAR_CHECKOUT_DATA'
})

const setStoredShippingData = () => dispatch => {
  dispatch({
    type: 'SET_STORED_SHIPPING_DATA'
  })
  dispatch(_setCountryStatesIfRequired()).then(() => {
    dispatch(_calculateShippingCost())
    dispatch(checkFormValidation())
  })
}

const checkFormValidation = () => (dispatch, getState) => {
  const state = getState()
  const shippingForm = state.checkout.shippingForm
  const apiError =
    state.checkout.calculation && state.checkout.calculation.apiError
  dispatch({
    type: 'SET_FORM_VALIDATION',
    email: state.activeStore.source === 'ZEROTOUCH' ? isValid(shippingForm.email.value) : true,
    store:
      isValid(shippingForm.store.value) &&
      minLength(shippingForm.store.value, 5) &&
      shippingForm.store.value.toUpperCase() !== 'MYPOP',
    recipient_name: isValid(shippingForm.recipient_name.value),
    country_code: isValid(shippingForm.country_code.value),
    state: isValid(shippingForm.state.value),
    line_1: isValid(shippingForm.line_1.value),
    city: isValid(shippingForm.city.value),
    postal_code: isValid(shippingForm.postal_code.value),
    ...(apiError &&
      apiError.postal_code &&
      (apiError.postal_code
        ? { postal_code_api_error: apiError.postal_code }
        : { postal_code_api_error: false }))
  })

  function isValid (value) {
    return value !== ''
  }

  function minLength (value, minLength) {
    return value.length >= minLength
  }
}

const blurInput = name => ({
  type: 'SET_INPUT_BLUR',
  name
})

const setCheckoutOrderId = id => ({
  type: 'SET_CHECKOUT_ORDER_ID',
  id
})

const setMissingProducts = products => ({
  type: 'SET_MISSING_PRODUCTS',
  missingProducts: products
})

function _setCountryStatesIfRequired () {
  return (dispatch, getState) => {
    const state = getState()
    const selectedCountryCode = state.checkout.shippingForm.country_code.value
    const countryObj = state.checkout.countriesList.find(obj => {
      return obj.code_2 === selectedCountryCode
    })
    return dispatch(
      getStatesForCountry(
        selectedCountryCode,
        countryObj && countryObj.has_state_codes
      )
    )
  }
}

function _calculateShippingCost () {
  return (dispatch, getState) => {
    const state = getState()
    const { activeStore, cart, checkout } = state
    // const isPickupSelected = checkout.isPickupSelected
    const shippingForm = new ShippingModel(checkout.shippingForm)
    // const shippingInfoRequired = cart.hasShippingProduct() && !isPickupSelected && activeStore.source !== 'ZEROTOUCH'
    const storeGeometry = activeStore.geometry
    const shipping = checkout.isShippingSelected ? shippingForm.getFlatObject() : null
    return dispatch(
      calculateCost(
        activeStore.shortId,
        cart.getProducts(),
        storeGeometry,
        shipping,
        checkout.curatorToken,
        checkout.tableNumber,
        checkout.isTablePickupSelected
      )
    )
  }
}

function _setInputValue (name, value) {
  return {
    type: 'SET_INPUT_VALUE',
    name,
    value
  }
}

export {
  blurInput,
  checkFormValidation,
  calculateCost,
  clearCheckout,
  highlightInvalid,
  getStatesForCountry,
  setPaypalLoadedApi,
  setPaymentStatus,
  setCancelIndicator,
  getShippingCountries,
  setInputValue,
  setStoredShippingData,
  setPaypalIsPending,
  setPickupOption,
  setTablePickupOption,
  setTableNumber,
  setCheckoutOrderId,
  setMissingProducts,
  setPostalCodeInputValue,
  setCheckoutErrors,
  setStateInputValue,
  setCountryCodeInputValue,
  setCuratorToken,
  setServeToTableOption,
  setShippingOption
}
